import React from 'react'
import type { DrawerProps } from '@/ui/components'
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerTitle,
} from '@/ui/components'
import { graphql } from 'babel-plugin-relay/macro'
import { useFragment } from 'react-relay-offline'
import { Text } from '@vtex/admin-ui'
import { useIntl } from 'react-intl'

import type { ProductSpecificationsDrawer_Product$key } from './__generated__/ProductSpecificationsDrawer_Product.graphql'
import { ProductSpecificationsContent } from '.'

export const productSpecificationsFragment = graphql`
  fragment ProductSpecificationsDrawer_Product on Product {
    brand
    specificationGroups {
      edges {
        node {
          name
          specifications {
            edges {
              node {
                name
                values
              }
            }
          }
        }
      }
    }
  }
`

type ProductSpecificationsDrawerProps = DrawerProps & {
  productSpecifications: ProductSpecificationsDrawer_Product$key | null
}

export const ProductSpecificationsDrawer = ({
  productSpecifications,
  ...props
}: ProductSpecificationsDrawerProps) => {
  const data = useFragment(productSpecificationsFragment, productSpecifications)
  const { formatMessage } = useIntl()

  if (!data?.specificationGroups) {
    return null
  }

  return (
    <Drawer {...props} data-testid="product-specifications-drawer">
      <DrawerTitle>
        {formatMessage({ id: 'productSpecificationsDrawerTitle' })}
      </DrawerTitle>
      <DrawerCloseButton />
      <DrawerBody>
        <ProductSpecificationsContent>
          <Text variant="body">
            {formatMessage({ id: 'productBrandLabel' })}
          </Text>
          <div>
            <Text
              tone="secondary"
              dangerouslySetInnerHTML={{ __html: data.brand }}
            />
          </div>
        </ProductSpecificationsContent>
        {data?.specificationGroups.edges.map((groupEdge) =>
          groupEdge.node.specifications.edges.map((edgeSpecification) => (
            <ProductSpecificationsContent key={edgeSpecification.node.name}>
              <Text variant="body">{edgeSpecification.node.name}</Text>
              <div>
                {edgeSpecification.node.values &&
                  Array.isArray(edgeSpecification.node.values) &&
                  edgeSpecification.node.values.map((value) => (
                    <Text
                      tone="secondary"
                      key={value}
                      dangerouslySetInnerHTML={{ __html: value }}
                    />
                  ))}
              </div>
            </ProductSpecificationsContent>
          ))
        )}
      </DrawerBody>
    </Drawer>
  )
}
